import React, { useState, useMemo, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { codingStudentService } from '@/services/coding/codingStudentService';
import type {
  StudentCodingProblemDetails,
  StudentCodingProblemListItem,
} from '@/services/coding/codingStudentService';
import { useStudentTheme } from '@context/StudentThemeContext';
import CodingTaskPanel from './CodingTaskPanel';
import PythonBasicsReadingMaterial from './PythonBasicsReadingMaterial';
import {
  BookOpen,
  ArrowLeft,
  ArrowRight,
  Code2,
  CheckCircle2,
  CircleDot,
  Clock,
  Zap,
  PartyPopper,
  Loader2,
  Layers,
  ChevronRight,
  Sparkles,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useRipple } from '@/hooks/useRipple';

type ViewMode = 'overview' | 'reading' | 'problem' | 'complete';

const WeekFlowPage: React.FC = () => {
  const { weekNumber: weekNumberStr } = useParams<{ weekNumber: string }>();
  const weekNumber = parseInt(weekNumberStr || '1', 10);
  const [searchParams, setSearchParams] = useSearchParams();
  const { theme } = useStudentTheme();
  const isDark = theme === 'dark';
  const ripple = useRipple();

  // URL state management
  const viewParam = searchParams.get('view') as ViewMode | null;
  const problemIndexParam = searchParams.get('p');

  const [currentView, setCurrentView] = useState<ViewMode>(
    viewParam === 'reading' || viewParam === 'problem' || viewParam === 'complete'
      ? viewParam
      : 'overview'
  );

  const [currentProblemIndex, setCurrentProblemIndex] = useState<number>(
    problemIndexParam ? Math.max(0, parseInt(problemIndexParam, 10)) : 0
  );

  // Sync state with URL params
  useEffect(() => {
    if (viewParam === 'reading') {
      setCurrentView('reading');
    } else if (viewParam === 'problem') {
      setCurrentView('problem');
      if (problemIndexParam) {
        setCurrentProblemIndex(Math.max(0, parseInt(problemIndexParam, 10)));
      }
    } else if (viewParam === 'complete') {
      setCurrentView('complete');
    } else {
      setCurrentView('overview');
    }
  }, [viewParam, problemIndexParam]);

  const setViewInUrl = (view: ViewMode, problemIdx?: number) => {
    const params = new URLSearchParams();
    if (view !== 'overview') {
      params.set('view', view);
      if (view === 'problem' && problemIdx !== undefined) {
        params.set('p', problemIdx.toString());
      }
    }
    setSearchParams(params);
    setCurrentView(view);
    if (problemIdx !== undefined) {
      setCurrentProblemIndex(problemIdx);
    }
  };

  // Fetch problems for student
  const { data: problemsResponse, isLoading: problemsLoading } = useQuery({
    queryKey: ['student-coding-problems'],
    queryFn: () => codingStudentService.getStudentProblems(),
  });

  const allProblems: StudentCodingProblemListItem[] = problemsResponse?.data?.data || [];
  const weekProblems = useMemo(
    () =>
      allProblems
        .filter((p) => (p.week_number || 1) === weekNumber)
        .sort((a, b) => a.order_no - b.order_no),
    [allProblems, weekNumber]
  );

  // Calculate statistics
  const totalSolved = weekProblems.filter((p) => p.student_status === 'SOLVED').length;
  const totalAttempted = weekProblems.filter((p) => p.student_status === 'ATTEMPTED').length;
  const weekProgress = weekProblems.length > 0 ? Math.round((totalSolved / weekProblems.length) * 100) : 0;

  const currentProblemSummary = weekProblems[currentProblemIndex] || null;
  const currentProblemId = currentProblemSummary?.id || null;

  // Fetch current active problem details
  const { data: problemDetailsResponse, isLoading: problemDetailsLoading } = useQuery({
    queryKey: ['student-problem-details', currentProblemId],
    queryFn: () => codingStudentService.getProblemDetails(currentProblemId!),
    enabled: currentView === 'problem' && !!currentProblemId,
  });

  const currentProblemDetails: StudentCodingProblemDetails | null =
    problemDetailsResponse?.data?.data || null;

  const getDifficultyPill = (diff: string) => {
    switch (diff?.toUpperCase()) {
      case 'EASY':
        return (
          <span className="px-2.5 py-0.5 rounded-full text-[10px] font-extrabold bg-emerald-50 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800">
            Easy
          </span>
        );
      case 'MEDIUM':
        return (
          <span className="px-2.5 py-0.5 rounded-full text-[10px] font-extrabold bg-amber-50 text-amber-700 dark:bg-amber-950/60 dark:text-amber-400 border border-amber-200 dark:border-amber-800">
            Medium
          </span>
        );
      case 'HARD':
        return (
          <span className="px-2.5 py-0.5 rounded-full text-[10px] font-extrabold bg-rose-50 text-rose-700 dark:bg-rose-950/60 dark:text-rose-400 border border-rose-200 dark:border-rose-800">
            Hard
          </span>
        );
      default:
        return <span>{diff}</span>;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'SOLVED':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800 dark:bg-emerald-900/60 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-700">
            <CheckCircle2 size={12} className="text-emerald-700 dark:text-emerald-400" />
            Solved
          </span>
        );
      case 'ATTEMPTED':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-amber-50 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300 border border-amber-200 dark:border-amber-800">
            <CircleDot size={12} className="text-amber-600" />
            Attempted
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-semibold bg-gray-100 text-gray-500 dark:bg-slate-800 dark:text-slate-400 border border-gray-200 dark:border-slate-700">
            <Clock size={12} />
            Unsolved
          </span>
        );
    }
  };

  // Navigations
  const handleNextFromReading = () => {
    if (weekProblems.length > 0) {
      setViewInUrl('problem', 0);
    } else {
      setViewInUrl('overview');
    }
  };

  const handlePreviousFromProblem = () => {
    if (currentProblemIndex === 0) {
      setViewInUrl('reading');
    } else {
      setViewInUrl('problem', currentProblemIndex - 1);
    }
  };

  const handleNextFromProblem = () => {
    if (currentProblemIndex >= weekProblems.length - 1) {
      setViewInUrl('complete');
      toast.success('Coding tasks complete! Amazing work!', { duration: 4000 });
    } else {
      setViewInUrl('problem', currentProblemIndex + 1);
    }
  };

  if (problemsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 size={32} className="animate-spin text-primary" />
      </div>
    );
  }

  /* ─────────────────────────────────────────────────────────────
     1. FULL LANDING / OVERVIEW VIEW (Curriculum content list)
  ───────────────────────────────────────────────────────────── */
  if (currentView === 'overview') {
    return (
      <div className="space-y-6 max-w-5xl mx-auto">
        {/* Top Active Module Badge */}
        <div className="flex items-center justify-end">
          <span className="text-xs font-bold px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800">
            Active Module
          </span>
        </div>

        {/* Hero Banner with Progress */}
        <div className={`rounded-3xl border p-6 sm:p-8 shadow-soft flex flex-col md:flex-row md:items-center justify-between gap-6 relative overflow-hidden ${
          isDark ? 'bg-slate-900 border-slate-700' : 'bg-white border-border'
        }`}>
          <div className="space-y-2 max-w-xl">
            <div className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-wider text-primary">
              <Layers size={16} />
              Coding Tasks Curriculum
            </div>
            <h1 className={`text-2xl sm:text-3xl font-black ${isDark ? 'text-white' : 'text-navy'}`}>
              Python Basics - Lists to Loops
            </h1>
            <p className={`text-xs sm:text-sm leading-relaxed ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              Master fundamentals from basic input/output to lists, conditions, and loops. Complete the reading material first, then solve all sequential practice problems.
            </p>

            <div className="pt-2 flex items-center gap-3">
              <button
                type="button"
                onClick={() => setViewInUrl('reading')}
                onPointerDown={ripple.onPointerDown}
                className="ripple-trigger inline-flex items-center gap-2 px-5 py-2.5 bg-primary hover:bg-primary/90 text-white rounded-xl text-xs font-black transition-all cursor-pointer shadow-sm"
              >
                <BookOpen size={15} />
                Start Reading Material
              </button>

              {weekProblems.length > 0 && (
                <button
                  type="button"
                  onClick={() => setViewInUrl('problem', 0)}
                  onPointerDown={ripple.onPointerDown}
                  className={`ripple-trigger inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer border ${
                    isDark
                      ? 'bg-slate-800 border-slate-700 text-slate-200 hover:bg-slate-700'
                      : 'bg-gray-50 border-gray-200 text-slate-700 hover:bg-gray-100'
                  }`}
                >
                  <Code2 size={15} />
                  Jump to Problems
                </button>
              )}
            </div>
          </div>

          {/* Progress Widget */}
          <div className={`w-full md:w-72 rounded-2xl p-5 border space-y-3 shrink-0 ${
            isDark ? 'bg-slate-800/60 border-slate-700' : 'bg-blue-bg/60 border-border'
          }`}>
            <div className="flex justify-between items-center text-xs font-bold">
              <span className={`flex items-center gap-1.5 ${isDark ? 'text-slate-300' : 'text-navy'}`}>
                <Zap size={14} className="text-primary" /> Module Progress
              </span>
              <span className="text-primary font-black">{weekProgress}%</span>
            </div>
            <div className={`w-full rounded-full h-2.5 ${isDark ? 'bg-slate-700' : 'bg-gray-200'}`}>
              <div
                className="bg-primary h-2.5 rounded-full transition-all duration-1000"
                style={{ width: `${weekProgress}%` }}
              />
            </div>
            <div className={`flex justify-between text-[11px] font-semibold pt-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              <span><b className={isDark ? 'text-emerald-400' : 'text-emerald-700'}>{totalSolved}</b> Solved</span>
              <span><b className={isDark ? 'text-amber-400' : 'text-amber-700'}>{totalAttempted}</b> Attempted</span>
              <span><b className={isDark ? 'text-slate-200' : 'text-navy'}>{weekProblems.length}</b> Tasks</span>
            </div>
          </div>
        </div>

        {/* Content Roadmap List */}
        <div className="space-y-3">
          <div className="flex items-center justify-between px-1">
            <h2 className={`text-base font-bold flex items-center gap-2 ${isDark ? 'text-white' : 'text-navy'}`}>
              <Sparkles size={18} className="text-amber-500" />
              Learning Roadmap &amp; Exercises ({weekProblems.length + 1} Items)
            </h2>

          </div>

          {/* ITEM 0: Reading Material Card */}
          <div
            onClick={() => setViewInUrl('reading')}
            className={`group rounded-2xl border p-5 shadow-soft hover:shadow-card hover:-translate-y-0.5 transition-all duration-200 cursor-pointer flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-l-4 border-l-amber-500 select-none ${
              isDark ? 'bg-slate-900 border-slate-700 hover:border-primary/50' : 'bg-white border-border hover:border-primary/50'
            }`}
          >
            <div className="flex items-start sm:items-center gap-4 cursor-pointer">
              <div className="w-10 h-10 rounded-xl bg-amber-50 dark:bg-amber-950/60 text-amber-600 dark:text-amber-400 flex items-center justify-center font-black text-sm shrink-0 border border-amber-200 dark:border-amber-800">
                01
              </div>
              <div className="space-y-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/60 text-amber-800 dark:text-amber-300">
                    Theory &bull; Required
                  </span>
                  <span className="text-[10px] font-bold text-slate-400">
                    Estimated: 15-20 mins
                  </span>
                </div>
                <h3 className={`text-base font-bold group-hover:text-primary transition-colors ${isDark ? 'text-white' : 'text-navy'}`}>
                  Python Basics - Reading Material
                </h3>
                <p className={`text-xs line-clamp-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                  Read this before the practice: Google Colab, input/output patterns, variables, conditions, loops, and lists.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3 shrink-0 self-end sm:self-center">
              <button
                type="button"
                onPointerDown={ripple.onPointerDown}
                className="ripple-trigger inline-flex items-center gap-1.5 px-4 py-2 bg-amber-50 hover:bg-amber-600 text-amber-800 hover:text-white dark:bg-amber-950/60 dark:text-amber-300 dark:hover:bg-amber-600 dark:hover:text-white rounded-xl text-xs font-bold transition-all border border-amber-200 dark:border-amber-800 cursor-pointer shadow-xs"
              >
                <BookOpen size={13} />
                Read Material
                <ArrowRight size={13} />
              </button>
            </div>
          </div>

          {/* Practice Problem Cards (1 to N) */}
          {weekProblems.map((prob, idx) => (
            <div
              key={prob.id}
              onClick={() => setViewInUrl('problem', idx)}
              className={`group rounded-2xl border p-5 shadow-soft hover:shadow-card hover:-translate-y-0.5 transition-all duration-200 cursor-pointer flex flex-col sm:flex-row sm:items-center justify-between gap-4 select-none ${
                prob.student_status === 'SOLVED'
                  ? isDark ? 'bg-slate-900/90 border-emerald-900/60 border-l-4 border-l-emerald-500' : 'bg-white border-emerald-200 border-l-4 border-l-emerald-500'
                  : isDark ? 'bg-slate-900 border-slate-700 hover:border-primary/50' : 'bg-white border-border hover:border-primary/50'
              }`}
            >
              <div className="flex items-start sm:items-center gap-4 cursor-pointer">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-mono font-bold text-xs shrink-0 border ${
                  prob.student_status === 'SOLVED'
                    ? 'bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800'
                    : isDark ? 'bg-slate-800 text-slate-400 border-slate-700' : 'bg-gray-100 text-slate-500 border-gray-200'
                }`}>
                  {idx + 2 < 10 ? `0${idx + 2}` : idx + 2}
                </div>

                <div className="space-y-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    {getDifficultyPill(prob.difficulty)}
                    <span className="text-[10px] font-mono text-slate-400 px-1.5 py-0.5 bg-gray-50 dark:bg-slate-800 rounded">
                      Python 3
                    </span>
                    {getStatusBadge(prob.student_status)}
                  </div>

                  <h3 className={`text-base font-bold group-hover:text-primary transition-colors ${isDark ? 'text-white' : 'text-navy'}`}>
                    {prob.title}
                  </h3>

                  <p className={`text-xs line-clamp-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                    {prob.description.replace(/[#*`_]/g, '')}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3 shrink-0 self-end sm:self-center">
                {prob.best_execution_time_ms != null && (
                  <span className="text-[11px] font-mono font-bold text-emerald-600 flex items-center gap-1">
                    <Zap size={12} />
                    {Math.round(prob.best_execution_time_ms)}ms
                  </span>
                )}

                <button
                  type="button"
                  onPointerDown={ripple.onPointerDown}
                  className={`ripple-trigger inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold transition-all shadow-xs cursor-pointer ${
                    prob.student_status === 'SOLVED'
                      ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-600 hover:text-white border border-emerald-200 dark:bg-emerald-950/60 dark:text-emerald-400 dark:border-emerald-800'
                      : 'bg-primary text-white hover:bg-primary/90'
                  }`}
                >
                  {prob.student_status === 'SOLVED' ? 'Review Solution' : 'Solve Problem'}
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  /* ─────────────────────────────────────────────────────────────
     2. FULL WIDTH READING MATERIAL VIEW (Styled after HTML spec)
  ───────────────────────────────────────────────────────────── */
  if (currentView === 'reading') {
    return (
      <div className="space-y-4 max-w-4xl mx-auto">
        {/* Top Header Bar with Back Button */}
        <div className={`p-4 rounded-2xl border shadow-soft flex items-center justify-between gap-3 ${
          isDark ? 'bg-slate-900 border-slate-700' : 'bg-white border-border'
        }`}>
          <button
            type="button"
            onClick={() => setViewInUrl('overview')}
            onPointerDown={ripple.onPointerDown}
            className="ripple-trigger inline-flex items-center gap-2 text-xs font-bold text-slate-600 dark:text-slate-300 hover:text-primary transition-colors cursor-pointer"
          >
            <ArrowLeft size={16} />
            Back to Coding Tasks
          </button>

          <span className="text-[11px] font-bold px-3 py-1 rounded-full bg-primary/10 text-primary">
            Step 1 of {weekProblems.length + 1}
          </span>
        </div>

        {/* Body: High fidelity reading material component */}
        <PythonBasicsReadingMaterial />

        {/* Bottom Nav Footer */}
        <div className={`p-4 rounded-2xl border shadow-soft flex items-center justify-between gap-3 shrink-0 ${
          isDark ? 'bg-slate-900 border-slate-700' : 'bg-white border-border'
        }`}>
          <button
            type="button"
            onClick={() => setViewInUrl('overview')}
            onPointerDown={ripple.onPointerDown}
            className={`ripple-trigger inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
              isDark ? 'bg-slate-800 hover:bg-slate-700 text-slate-200' : 'bg-gray-100 hover:bg-gray-200 text-slate-700'
            }`}
          >
            <ArrowLeft size={15} />
            Back to Coding Tasks
          </button>

          <span className="text-xs font-bold text-slate-400 hidden sm:inline">
            Finished reading? Start the practice challenges!
          </span>

          <button
            type="button"
            onClick={handleNextFromReading}
            onPointerDown={ripple.onPointerDown}
            className="ripple-trigger inline-flex items-center gap-2 px-6 py-2.5 bg-primary hover:bg-primary/90 text-white rounded-xl text-xs font-black transition-all cursor-pointer shadow-sm"
          >
            Next: Practice Problems
            <ArrowRight size={15} />
          </button>
        </div>
      </div>
    );
  }

  /* ─────────────────────────────────────────────────────────────
     3. FULL WIDTH CODING PROBLEM VIEW (CodingTaskPanel IDE)
  ───────────────────────────────────────────────────────────── */
  if (currentView === 'problem') {
    return (
      <div className="space-y-4">
        {/* Top Header Bar with Back Button */}
        <div className={`p-3.5 rounded-2xl border shadow-soft flex items-center justify-between gap-3 ${
          isDark ? 'bg-slate-900 border-slate-700' : 'bg-white border-border'
        }`}>
          <button
            type="button"
            onClick={() => setViewInUrl('overview')}
            onPointerDown={ripple.onPointerDown}
            className="ripple-trigger inline-flex items-center gap-2 text-xs font-bold text-slate-600 dark:text-slate-300 hover:text-primary transition-colors cursor-pointer"
          >
            <ArrowLeft size={16} />
            Back to Coding Tasks
          </button>

          <div className="flex items-center gap-2">
            <span className="text-xs font-mono font-bold text-slate-400">
              Problem {currentProblemIndex + 1} of {weekProblems.length}
            </span>
          </div>
        </div>

        {/* Body: Full width IDE */}
        {problemDetailsLoading || !currentProblemDetails ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 size={32} className="animate-spin text-primary" />
          </div>
        ) : (
          <CodingTaskPanel
            problem={currentProblemDetails}
            problemNumber={currentProblemIndex + 1}
            totalProblems={weekProblems.length}
            isLastProblem={currentProblemIndex === weekProblems.length - 1}
            onPrevious={handlePreviousFromProblem}
            onNext={handleNextFromProblem}
          />
        )}
      </div>
    );
  }

  /* ─────────────────────────────────────────────────────────────
     4. COMPLETION CELEBRATION VIEW
  ───────────────────────────────────────────────────────────── */
  return (
    <div className={`flex flex-col items-center justify-center py-20 rounded-3xl border text-center space-y-6 shadow-soft max-w-xl mx-auto ${
      isDark ? 'bg-slate-900 border-slate-700' : 'bg-white border-border'
    }`}>
      <div className="w-20 h-20 rounded-3xl bg-emerald-50 dark:bg-emerald-950/60 border border-emerald-200 dark:border-emerald-800 flex items-center justify-center text-emerald-600">
        <PartyPopper size={44} />
      </div>

      <div className="space-y-2 px-4">
        <div className="text-xs font-extrabold uppercase tracking-widest text-emerald-600">
          Module Completed
        </div>
        <h2 className={`text-2xl sm:text-3xl font-black ${isDark ? 'text-white' : 'text-navy'}`}>
          Coding Tasks Complete!
        </h2>
        <p className={`text-xs sm:text-sm max-w-md mx-auto leading-relaxed ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
          Outstanding work! You have finished all the reading material and practice problems.
        </p>
      </div>

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => setViewInUrl('overview')}
          onPointerDown={ripple.onPointerDown}
          className="ripple-trigger inline-flex items-center gap-2 px-6 py-3 bg-primary hover:bg-primary/90 text-white rounded-xl text-xs font-bold transition-all cursor-pointer shadow-sm"
        >
          <Layers size={16} />
          View Tasks Roadmap
        </button>
      </div>
    </div>
  );
};

export default WeekFlowPage;
